// src/pages/guides/GuidesIndex.js
// Security Guides Index Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { GUIDES, guidesByCluster } from '../../config/guidesCatalog';
import GuideByline from './GuideByline';
import { PublicHeader, PublicFooter } from '../../components/common';
import { Reveal } from '../../components/common';
import './Guides.css';

function GuidesIndex() {
  const startTrial = useTrialCta('guide_trial:guides_index');

  // PRD-193 WP2: the inline 19-entry array that used to live here now lives in
  // frontend/src/config/guidesCatalog.js, the single source of truth. Order is preserved
  // exactly, so the rendered cards and the ItemList schema positions are unchanged.
  const guides = GUIDES;

  // PRD-193 WP6: the same guides, grouped into the 5 pillar-and-cluster groups. Each guide's
  // own `cluster` field drives the grouping, so nothing here needs editing to move a guide.
  const clusters = guidesByCluster();

  // Most recent dateModified across the library (ISO strings sort lexicographically).
  const libraryLastUpdated = guides
    .map(g => g.dateModified)
    .filter(Boolean)
    .sort()
    .pop();

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "DriftAlarm Security Guides",
    "description": "Practical security guides for SMB IT leaders.",
    "numberOfItems": guides.length,
    "itemListElement": guides.map((guide, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": guide.title,
      "url": `https://www.driftalarm.com${guide.path}`
    }))
  };

  // PRD-193 WP6: CollectionPage declares what this page IS -- a curated library organised
  // into named sections -- which ItemList alone does not say. `hasPart` names the clusters so
  // the structure a reader sees is the structure a machine reads.
  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "DriftAlarm Security Guides",
    "description": "Practical, tool-agnostic external attack surface guides for IT teams without dedicated security headcount, organised into five clusters.",
    "url": "https://www.driftalarm.com/guides",
    "isPartOf": { "@type": "WebSite", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "hasPart": clusters.map(c => ({
      "@type": "Collection",
      "name": c.name,
      ...(c.intro ? { "description": c.intro } : {}),
      "url": `https://www.driftalarm.com/guides#${c.slug}`,
      "hasPart": c.guides.map(g => ({
        "@type": "Article",
        "headline": g.title,
        "url": `https://www.driftalarm.com${g.path}`
      }))
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Security Guides | DriftAlarm"
        description="Practical security guides for SMB IT leaders. Learn Attack Surface Management (ASM), vulnerability monitoring, exposure management, and incident response—tool-agnostic and actionable."
        path="/guides"
        jsonLd={[collectionPageSchema, itemListSchema, breadcrumbSchema]}
      />
      {/* Navigation Bar */}
      <PublicHeader activeLink="guides" />

      {/* Main Content */}
      <main className="guide-content">
        {/* Hero Section */}
        <section className="guides-index-hero">
          <h1>Security Guides</h1>
          <p className="guides-index-intro">
            Practical, tool-agnostic guides for SMB IT and security leaders.
            Each guide gives you actionable frameworks you can implement today—no
            enterprise budget required.
          </p>
          {/* PRD-193 WP3: the index has no dateModified of its own, so it carries the most
              recent guide update -- the honest answer to "how fresh is this library?". */}
          <GuideByline dateModified={libraryLastUpdated} label="Library last updated" />
        </section>

        {/* PRD-193 WP6: grouped into 5 clusters instead of one flat 19-card dump.
            The cluster heading is the h2 and the card title drops to h3, so the page keeps a
            single coherent heading hierarchy rather than 19 sibling h2s under the h1. */}
        {clusters.map(cluster => (
          <section className="guides-cluster" id={cluster.slug} key={cluster.slug}>
            <div className="guides-cluster-header">
              <h2 className="guides-cluster-title">{cluster.name}</h2>
              {cluster.tagline && (
                <p className="guides-cluster-tagline">{cluster.tagline}</p>
              )}
              {cluster.intro && (
                <p className="guides-cluster-intro">{cluster.intro}</p>
              )}
            </div>

            <div className="guides-grid">
              {cluster.guides.map((guide, index) => (
                <Reveal as={Link} to={guide.path} key={guide.slug} className="guide-card" delay={(index % 3) * 80}>
                  <div className="guide-card-category">{guide.category}</div>
                  <h3 className="guide-card-title">{guide.title}</h3>
                  <p className="guide-card-description">{guide.description}</p>
                  <div className="guide-card-footer">
                    <span className="guide-card-read-time">{guide.readTime}</span>
                    <span className="guide-card-link">Read Guide →</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        ))}

        {/* Related Solutions Section */}
        <Reveal as="section" className="guides-coming-soon" delay={0}>
          <h2>Related Solutions</h2>
          <p>See how DriftAlarm continuously monitors for these threats:</p>
          <div className="coming-soon-grid">
            <Link to="/attack-surface-monitoring" className="coming-soon-item" style={{ textDecoration: 'none' }}>
              <span className="coming-soon-title">Attack Surface Monitoring</span>
              <span className="guide-card-link">Learn More →</span>
            </Link>
            <Link to="/domain-monitoring" className="coming-soon-item" style={{ textDecoration: 'none' }}>
              <span className="coming-soon-title">Domain Monitoring</span>
              <span className="guide-card-link">Learn More →</span>
            </Link>
            <Link to="/exposed-rdp-monitoring" className="coming-soon-item" style={{ textDecoration: 'none' }}>
              <span className="coming-soon-title">Exposed RDP Monitoring</span>
              <span className="guide-card-link">Learn More →</span>
            </Link>
            <Link to="/external-vulnerability-scanning" className="coming-soon-item" style={{ textDecoration: 'none' }}>
              <span className="coming-soon-title">Vulnerability Scanning</span>
              <span className="guide-card-link">Learn More →</span>
            </Link>
          </div>
        </Reveal>

      {/* Knowledge Base Sections - Anchored from tooltip "Learn more" links */}
      <Reveal as="section" className="kb-section" delay={0}>
        <div className="kb-container">
          <h2 className="kb-main-title">Knowledge Base</h2>
          <p className="kb-intro">Quick reference on key DriftAlarm concepts. These sections are linked from contextual help tooltips throughout the platform.</p>

          <div className="kb-toc">
            <a href="#risk-score">Risk Scores</a>
            <a href="#severity-levels">Severity Levels</a>
            <a href="#drift-detection">Drift Detection</a>
            <a href="#scan-types">Scan Types</a>
            <a href="#drift-alarms">Drift Alarms</a>
            <a href="#epss">EPSS Scores</a>
            <a href="#technologies">Technologies</a>
          </div>

          <article id="risk-score" className="kb-article">
            <h3>How Risk Scores Work</h3>
            <p>DriftAlarm assigns each asset a risk score from 0 (most vulnerable) to 100 (most secure). The score is calculated from multiple factors including the number and severity of open vulnerabilities, exposed high-risk ports (SSH, RDP, databases), SSL/TLS certificate health, and drift history.</p>
            <p>A score below 50 indicates significant risk requiring immediate attention. Scores between 50-75 suggest moderate risk with room for improvement. Scores above 75 indicate a well-secured asset. The score updates after every scan to reflect your current security posture.</p>
          </article>

          <article id="severity-levels" className="kb-article">
            <h3>Vulnerability Severity Levels</h3>
            <p>DriftAlarm uses industry-standard CVSS-based severity levels to classify vulnerabilities:</p>
            <ul className="kb-list">
              <li><strong>Critical (9.0-10.0)</strong> — Immediate action required. Remote code execution, authentication bypasses, or publicly known exploited vulnerabilities.</li>
              <li><strong>High (7.0-8.9)</strong> — Address soon. Significant security weaknesses that could lead to data exposure or unauthorized access.</li>
              <li><strong>Medium (4.0-6.9)</strong> — Plan remediation. Security misconfigurations or information disclosure issues.</li>
              <li><strong>Low (0.1-3.9)</strong> — Monitor. Minor issues with limited security impact.</li>
            </ul>
          </article>

          <article id="drift-detection" className="kb-article">
            <h3>How Drift Detection Works</h3>
            <p>Drift detection continuously monitors your assets for unexpected changes by comparing current scan results against a known-good baseline. When you first scan an asset, DriftAlarm captures a baseline snapshot of your attack surface — ports, services, subdomains, technologies, and vulnerabilities.</p>
            <p>On subsequent scans, every change is classified: positive changes (hardening, patching) are marked green, neutral changes (expected updates) are gray, and negative changes (new exposure, new vulnerabilities) trigger alerts. The drift system tracks 29 rules across 6 security packs.</p>
          </article>

          <article id="scan-types" className="kb-article">
            <h3>Scan Types: Fast vs Deep</h3>
            <p><strong>Fast Scan</strong> performs quick reconnaissance in 2-5 minutes. It covers the top 100 ports, SSL/TLS certificates, DNS records, HTTP analysis, technology detection, and AI-powered risk scoring. Ideal for quick checks and initial discovery.</p>
            <p><strong>Deep Scan</strong> runs exhaustive testing in 15-30 minutes. It includes full DNS enumeration, comprehensive web crawling, complete vulnerability scanning with 10,000+ Nuclei templates, and detailed service analysis. Use Deep Scan for thorough security assessments.</p>
          </article>

          <article id="drift-alarms" className="kb-article">
            <h3>Configuring Drift Alarms</h3>
            <p>Drift Alarms are policy-based rules that trigger when your attack surface changes in ways you care about. DriftAlarm includes 29 built-in rules organized into 6 security packs: Essential Security, Network Monitoring, Certificate Management, Compliance Readiness, DNS Monitoring, and Registration Monitoring.</p>
            <p>Each rule can be independently enabled or disabled, and you can create custom rules on Pro and Enterprise plans. Rules evaluate changes after every scan and generate events with configurable severity levels.</p>
          </article>

          <article id="epss" className="kb-article">
            <h3>Understanding EPSS Scores</h3>
            <p>EPSS (Exploit Prediction Scoring System) estimates the probability that a vulnerability will be actively exploited in the wild within the next 30 days. Scores range from 0 to 1, where higher values indicate greater exploitation likelihood.</p>
            <p>An EPSS score of 0.5 means there is a 50% chance of exploitation in the next month. DriftAlarm uses EPSS alongside CVSS severity to help you prioritize remediation — a medium-severity vulnerability with high EPSS may be more urgent than a high-severity vulnerability with low EPSS.</p>
          </article>

          <article id="technologies" className="kb-article">
            <h3>Technology Fingerprinting</h3>
            <p>DriftAlarm detects software technologies running on your assets through multiple methods: HTTP response header analysis, JavaScript library detection, HTML meta tag inspection, and service banner grabbing on open ports.</p>
            <p>Detected technologies are categorized into groups like Web Servers, Frameworks, Languages, Databases, CDN providers, and JavaScript Libraries. Knowing your technology stack helps identify version-specific vulnerabilities and track shadow IT.</p>
          </article>
        </div>
      </Reveal>

        {/* CTA Section */}
        <Reveal as="section" className="guides-cta-section" delay={0}>
          <h2>Ready to Monitor Your Attack Surface?</h2>
          <p>Put these guides into practice with DriftAlarm's continuous monitoring platform.</p>
          <button className="da-btn da-btn--primary" onClick={startTrial}>
            <span className="cta-shiny-text">Start Free 30-Day Trial</span>
            <span>→</span>
          </button>
        </Reveal>
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}

export default GuidesIndex;
